import { createState } from '../../lib/create-state';
import { getQuery } from '../../lib/query';
import { current } from './game';
import { isSuspended } from '../../lib/suspension';

export type AliasState = {
    checking: boolean;
    required: boolean;
    done: boolean;
    saving: boolean;
    errorKey: string;
};

type AliasResponse = {
    alias: string;
    change: boolean;
};

type AliasErrorResponse = {
    key?: string;
    message?: string;
};

const normalizeAliasErrorKey = (key?: string) => {
    if (key === 'error.invalid.alias') {
        return 'error.alias.invalid';
    }

    return key || 'error.unexpected';
};

export const DEFAULT_ALIAS_STATE: AliasState = {
    checking: false,
    required: false,
    done: false,
    saving: false,
    errorKey: '',
};

export const [alias, resetAlias] = createState(DEFAULT_ALIAS_STATE);

const normalizeInitialAlias = (initialAlias: string, userId: string) =>
    initialAlias === userId ? '' : initialAlias;

export const checkPlayerAlias = async (token: string) => {
    if (isSuspended()) return;
    alias.checking.set(true);

    try {
        const { USERID } = getQuery();
        const response = await fetch('/site-api/v2/player/alias', {
            redirect: 'follow',
            headers: {
                'x-session-token': token,
            },
        });

        if (isSuspended()) return;
        if (!response.ok) {
            alias.required.set(false);
            alias.done.set(true);
            return;
        }

        const data = (await response.json()) as AliasResponse;
        if (isSuspended()) return;

        current.player_alias.set(
            normalizeInitialAlias(data.alias || '', USERID || '')
        );
        alias.required.set(data.change);
        alias.done.set(!data.change);
        alias.errorKey.set('');
    } catch (error) {
        if (isSuspended()) return;
        alias.required.set(false);
        alias.done.set(true);
    } finally {
        if (!isSuspended()) alias.checking.set(false);
    }
};

export const savePlayerAlias = async (newAlias: string, token: string) => {
    if (isSuspended()) return;
    alias.saving.set(true);
    alias.errorKey.set('');

    try {
        const response = await fetch('/site-api/v2/player/alias', {
            method: 'PUT',
            redirect: 'follow',
            headers: {
                'content-type': 'application/json',
                'x-session-token': token,
            },
            body: JSON.stringify({
                alias: newAlias,
            }),
        });

        if (isSuspended()) return;
        if (response.status === 204) {
            current.player_alias.set(newAlias);
            alias.required.set(false);
            alias.done.set(true);
            return;
        }

        if (response.status === 422) {
            const data = (await response.json()) as AliasErrorResponse;
            if (isSuspended()) return;
            alias.errorKey.set(normalizeAliasErrorKey(data.key));
            return;
        }

        alias.errorKey.set('error.unexpected');
    } catch (error) {
        if (isSuspended()) return;
        alias.errorKey.set('error.unexpected');
    } finally {
        if (!isSuspended()) alias.saving.set(false);
    }
};
